import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  LOCALE_ID,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { PopoverModule } from 'primeng/popover';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { AssignedToComponent } from '@fitness-force/common/assigned-to'
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatestWith } from 'rxjs';
import {
  Claim,
  ClientCreditBalanceRequest,
  ClientCreditBalanceResponse,
  PaymentSignUp,
  STAFFINTERFACE,
  ValidationError,
} from '@fitness-force/models';
import { Roles } from '@fitness-force/enum';
import {
  COUNTRY_MASTER_STATE,
  STAFF_STATE,
} from 'apps/ff-frontend/src/app/state/app.state';
import { Store } from '@ngrx/store';
import { AppErrors, ErrorMsgComponent } from '@fitness-force/errors';
import { InputTextModule } from 'primeng/inputtext';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { StyleClassModule } from 'primeng/styleclass';
import { ErrorsComponent,} from '@fitness-force/common/errors'
import {
  AdvancedSettingService,
  AuthSevices,
  ClaimService,
  ClientService,
  CommonService,
  TimeconversionService,
} from '@fitness-force/api';
import { DialogModule } from 'primeng/dialog';
import {  ShortNamePipe} from '@fitness-force/pipes/short-name'
import { DatePickerModule } from 'primeng/datepicker';
import { OtherPaymentComponent } from '@fitness-force/common/payment';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { CommonModule, getCurrencySymbol } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { InputNumberComponent } from 'libs/shared/src/lib/components/input-number/input-number.component';

@Component({
  selector: 'credit-balance-sidebar',
  templateUrl: './credit-balance-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputNumberModule,
    ButtonModule,
    DrawerModule,
    AvatarModule,
    AvatarGroupModule,
    SelectModule,
    TooltipModule,
    DividerModule,
    PopoverModule,
    CurrencyformatPipe,
    AssignedToComponent,
    ErrorsComponent,
    ErrorMsgComponent,
    StyleClassModule,
    InputTextModule,
    ScrollPanelModule,
    DialogModule,
    ShortNamePipe,
    DatePickerModule,
    OtherPaymentComponent,
    SkeletonModule,
    InputNumberComponent,
    TranslateModule
  ]
})
export class CreditBalanceSidebarComponent implements OnInit {
  @Input('visible') showCreditSidebar = false;
  @Input('clientId') clientId = 0;
  @Input('tenantId') tenantId = 0;
  @Output() closeSidebar: EventEmitter<{ closeSidebar: boolean; uuid: string; }> = new EventEmitter();

  _filterStaff = new BehaviorSubject<number[]>([0]);
  claim$: Observable<Claim[]> = new Observable();
  creditBalanceForm: UntypedFormGroup = new UntypedFormGroup({});
  billingNameForm: UntypedFormGroup = new UntypedFormGroup({
    billing_name: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(1),
    ]),
  });
  changeBillingName = false;
  clientData: any;
  otherPaymentsModal$ = new BehaviorSubject(false);
  totalBalance$ = new BehaviorSubject(0);
  _selectedstaff = new BehaviorSubject<number>(0);
  currencySymbol$ = new BehaviorSubject<string>('');
  backedDatedDays: any = 0;
  disableDatesUpTo: Date = new Date();
  paymentDate: Date;
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;
  errorDataModal = [] as ValidationError[];
  error = new BehaviorSubject(null as AppErrors | null);
  isSubmitting: boolean = false;
  showSkeleton = new BehaviorSubject(true);

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private clientService: ClientService,
    private claimService: ClaimService,
    private authService: AuthSevices,
    private commonService: CommonService,
    private advanceSettingService: AdvancedSettingService,
    private timeService: TimeconversionService,
    private cd: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string
  ) { }

  ngOnInit() {
    this.creditBalanceForm = this.fb.group({
      payment_date: new UntypedFormControl(new Date(), []),
      billingOwner: new UntypedFormControl('', [Validators.required]),
      credit_amount: new UntypedFormControl('', [Validators.required]),
    });
    this.componentInit();
  }

  componentInit() {
    this.claim$ = this.claimService.GetClaim(environment.BASE_URL);
    this.getClientDetails();
    this.filterStaff();
    this.getAdvanceSetting();
    this.store.select(COUNTRY_MASTER_STATE).subscribe((country) => {
      this.currencySymbol$.next(
        getCurrencySymbol(country.currency_code, 'narrow', this.locale)
      );
    });
  }

  getClientDetails() {
    this.clientService.getClientById(this.clientId).subscribe({
      next: (res: any) => {
        this.clientData = res;
        this.showSkeleton.next(false);
      },
      error: () => {
        this.showSkeleton.next(false);
      },
    });
  }

  filterStaff() {
    let claim: Claim[] = {} as Claim[];
    let staffs: STAFFINTERFACE[] = [];
    let validStaff: number[] = [];
    this.claim$
      .pipe(combineLatestWith(this.store.select(STAFF_STATE)))
      .subscribe(([objClaim, objStaff]) => {
        claim = objClaim;
        staffs = objStaff.staffList;
        if (staffs.length > 0) {
          staffs.forEach((stf) => {
            if (
              stf.role == Roles.Admin ||
              stf.role.toString() == Roles[Roles.Admin] ||
              stf.role == Roles.FFAdmin ||
              stf.role.toString() == Roles[Roles.FFAdmin] ||
              claim.filter(
                (o) =>
                  o.id == stf.claim_template_id &&
                  (o.can_admin_billing == true || o.can_do_billing == true)
              ).length > 0
            )
              validStaff.push(stf.id);
          });
          this._filterStaff.next(validStaff);
        }
        if (this.authService.currentUserDetails()) {
          var currentUserDetails = this.authService.currentUserDetails();
          if (
            currentUserDetails.role.toLowerCase() == 'admin' ||
            currentUserDetails.can_do_billing == 'true' ||
            currentUserDetails.can_admin_billing == 'true'
          ) {
            this._selectedstaff.next(Number(this.authService.loggedInStaffId));
          }
        }
      });
  }

  getAdvanceSetting() {
    let tenant_settings = this.advanceSettingService.getAdvanceSetting(
      this.tenantId,
      'backdated_billing_setting'
    );
    if (tenant_settings) {
      tenant_settings.subscribe({
        next: (res) => {
          this.backedDatedDays = res.value;
          const currentDate = new Date();
          this.disableDatesUpTo = new Date(
            currentDate.setDate(currentDate.getDate() - this.backedDatedDays)
          );
        },
      });
    }
  }

  clientStatus(client: any): string {
    if (client && client.client_status)
      return this.clientService.getClientStatusHtml(
        client.client_status.toString()
      );
    else return '';
  }

  showPaymentModule() {
    if (this.creditBalanceForm.valid) {
      this.isSubmitting = true;
      this.otherPaymentsModal$.next(true);
    }
    else {
      Object.keys(this.creditBalanceForm.controls).forEach((field) => {
        const control = this.creditBalanceForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  removeBillingName() {
    this.billingNameFormControl.setValue('');
  }

  HideOtherPaymentDialog(evt: any) {
    if (evt && evt.paymentSignUp && evt.paymentSignUp.amount_in_minor_currency) {
      let paymentdetails = evt.paymentSignUp as PaymentSignUp;
      this.isSubmitting = true;
      let clientCreditBalanceRequest: ClientCreditBalanceRequest = {} as ClientCreditBalanceRequest;
      clientCreditBalanceRequest.collected_at_center = this.tenantId;
      clientCreditBalanceRequest.comments = '';
      clientCreditBalanceRequest.credit_balance_amount_in_minor_currency = evt.paymentSignUp.amount_in_minor_currency;
      clientCreditBalanceRequest.is_offline_payment = true;
      clientCreditBalanceRequest.payment_date = this.timeService.ConvertLocalDateTimeToUTC(this.PaymentDateFormControl.value);
      clientCreditBalanceRequest.sales_rep_id = this.billingOwner.value ? this.billingOwner.value.id : 0;
      clientCreditBalanceRequest.offline_payment = paymentdetails;
      this.clientService.AddClientCreditBalance(
        this.tenantId,
        this.clientId,
        clientCreditBalanceRequest
      )
        .subscribe({
          next: (_objResponse: ClientCreditBalanceResponse) => {
            this.isSubmitting = false;
            this.closeSidebar.emit({
              closeSidebar: true,
              uuid: _objResponse.uuid,
            });
            this.cd.detectChanges();
          },
          error: (error) => {
            this.isSubmitting = false;
            this.error.next(error);
            this.errorMessages.push([]);
            this.showErrorPopup?.next(true);
            this.cd.detectChanges();
          },
          complete: () => { },
        });
      this.otherPaymentsModal$.next(false);
    }
    else {
      this.otherPaymentsModal$.next(false);
      this.isSubmitting = false;
      this.cd.detectChanges();
    }
  }

  closeCreditSidebar() {
    this.isSubmitting = false;
    this.showCreditSidebar = false
    this.closeSidebar.emit({
      closeSidebar: true,
      uuid: "",
    });
  }

  HideErrorPopup() {
    this.errorMessages = [];
    this.errorDataModal = [];
    this.showErrorPopup?.next(false);
    this.isSubmitting = false;
    this.otherPaymentsModal$.next(false);
  }

  get creditBalance(): number {
    return this.commonService.ConvertMinorCurrencyFromAmount(
      this.CreditBalanceFormControl.value
    );
  }

  get billingOwner() {
    return this.creditBalanceForm.controls.billingOwner as UntypedFormControl;
  }

  get billingNameFormControl(): UntypedFormControl {
    return this.billingNameForm.controls.billing_name as UntypedFormControl;
  }

  get CreditBalanceFormControl(): UntypedFormControl {
    return this.creditBalanceForm.controls.credit_amount as UntypedFormControl;
  }

  get PaymentDateFormControl(): UntypedFormControl {
    return this.creditBalanceForm.controls.payment_date as UntypedFormControl;
  }
}
